// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Stack, Accordion, HStack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { IconTypes } from 'components/Icons/types';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { NetworkMapWrapper } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Network/NetworkMapWrapper';
import { SGNetworkMapWrapper } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Summary/Network/SecurityGroup/SGNetworkMapWrapper';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';

export const SecurityGroupNetworkTab: FC = () => {
  const {
    resourceDetails,
    resourceInsight,
    resourceType,
    resourceUuid,
    network,
  } = useSelector(selectActiveResource);
  const { openPorts } = network;
  const dispatch = useDispatch();
  const { data: resource } = resourceDetails;
  const { data: insight } = resourceInsight;

  const egress = resource?.network?.SecurityGroupRules?.filter(
    e => e?.IsEgress,
  );
  const ingress = resource?.network?.SecurityGroupRules?.filter(
    e => !e?.IsEgress,
  );

  useEffect(() => {
    if (
      resourceType === NativeResources.AzureVirtualMachine ||
      resourceType === NativeResources.GcpComputeEngine
    )
      dispatch(actions.getOpenPorts({ q: { resourceId: resourceUuid } }));
  }, [dispatch, resourceUuid, resourceType]);

  const prefixListColumns = [
    { header: 'Name', accessor: 'Name' },
    { header: 'CIDR', accessor: 'Cidr' },
  ];

  const columns = [
    {
      header: 'CIDR',
      accessor: 'CidrIpv4',
      render: ({ row }) =>
        `IPV${row?.CidrIpv6 ? `6: ${row?.CidrIpv6}` : `4: ${row?.CidrIpv4}`}`,
    },
    { header: 'Protocol', accessor: 'IpProtocol' },
    {
      header: 'From/To Port',
      render: ({ row }) => row?.FromPort + ' / ' + row?.ToPort,
    },
    {
      header: 'Rule ID',
      accessor: 'SecurityGroupRuleId',
      align: 'left',
    },
  ];

  return (
    <Stack w={'full'} h={'full'} pb={5} spacing={8}>
      <Box>
        <Accordion allowMultiple defaultIndex={[0]}>
          {(resourceType === NativeResources.AzureVirtualMachine ||
            resourceType === NativeResources.GcpComputeEngine) && (
            <MetaAccordionItem
              panel={
                <Table
                  columns={[
                    {
                      header: 'CIDR',
                      accessor: 'cidr',
                    },
                    {
                      header: 'Protocol',
                      accessor: 'protocol',
                    },
                    {
                      header: 'Port Range',
                      render: ({ row }) =>
                        row?.start_port + ' - ' + row?.end_port,
                    },
                  ]}
                  isLoading={openPorts.isLoading}
                  data={openPorts.data}
                  noDataMessage="No open ports"
                />
              }
              iconType={IconTypes.Port}
              label="Ingress open ports"
              height="400px"
              totalCount={openPorts.data?.length ?? 0}
            />
          )}
          {(resourceType === NativeResources.SecurityGroup ||
            resourceType === NativeResources.GcpCloudFirewall ||
            resourceType === NativeResources.AzureSecurityGroups) && (
            <>
              <MetaAccordionItem
                panel={<Table columns={columns} data={ingress} />}
                iconType={IconTypes.Ingress}
                label={
                  <HStack>
                    <Box>Ingress:</Box>
                    <Box>rules ({ingress?.length}),</Box>
                    <Box>open ports ({insight?.ingress_ports})</Box>
                  </HStack>
                }
                height="300px"
              />
              <MetaAccordionItem
                panel={
                  <Table
                    columns={columns}
                    data={egress}
                    subComponent={({ row }) => (
                      <Box height="250px">
                        <Table
                          data={
                            !isEmpty(row?.PrefixListIds)
                              ? row?.PrefixListIds
                              : row?.UserIdGroupPairs
                          }
                          columns={prefixListColumns}
                        />
                      </Box>
                    )}
                  />
                }
                iconType={IconTypes.Egress}
                label={
                  <HStack>
                    <Box>Egress:</Box>
                    <Box>rules ({egress?.length}),</Box>
                    <Box>open ports ({insight?.egress_ports})</Box>
                  </HStack>
                }
                height="300px"
              />
            </>
          )}
          <MetaAccordionItem
            iconType={IconTypes.NetworkMap}
            label={'Network map'}
            panel={
              resourceType === NativeResources.SecurityGroup ? (
                <SGNetworkMapWrapper />
              ) : (
                <NetworkMapWrapper />
              )
            }
            iconBGColor="orange"
            height="570px"
          />
        </Accordion>
      </Box>
    </Stack>
  );
};
